import React, { FC, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { FlatList, FlatListProps } from 'react-native';

import type { IHistoricalOrder } from '@rbi-ctg/menu';
import DrawerPage from 'components/layout/drawer-page';
import { RefreshButton } from 'components/refresh-button';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import useDebounceEffect from 'hooks/use-debounce-effect';
import { useUserOrders } from 'hooks/use-user-orders';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';

import Order from './order';
import OrdersEmpty from './orders-empty';
import { OfferDisclaimer, OrdersEmptyWrapper } from './styled';

function historicalOrderKeyExtractor(item: IHistoricalOrder, index: number) {
  return item?.rbiOrderId || index + '_order';
}

export const AccountOrders: FC = () => {
  // TODO: BKPE-1956 - unavailableCartEntries
  const { unavailableCartEntries } = useOrderContext();
  const { loading, orders, refetch } = useUserOrders({});
  const dismissAndNavigate = useDismissAllAndNavigate();

  const { formatMessage } = useIntl();
  const onRefreshPress = useCallback(() => {
    refetch();
  }, [refetch]);
  const [showOrderUnavailableModal, setShowOrderUnavailableModal] = useState(false);
  const handleUnavailableModalDismiss = useCallback(() => {
    setShowOrderUnavailableModal(false);
    dismissAndNavigate(routes.cart);
  }, [dismissAndNavigate]);

  /* This timeout is needed RN has an issue when you are redirecting from a modal to another page that
       has a modal it doesn't appear as seems that RN Modal controller on the native version is taking a bit
       of time to get into a stable state so weird behavior can happen like the modal is not showed,
       with this timeout we are taking a bit of time to get that state stable and open the modal.
       I didn't found any solution for this except this hack */
  useDebounceEffect(
    () => {
      if (unavailableCartEntries.length) {
        setShowOrderUnavailableModal(true);
      }
    },
    500,
    [unavailableCartEntries.length]
  );

  const refreshButton = (
    <RefreshButton onPress={onRefreshPress}>{formatMessage({ id: 'refresh' })}</RefreshButton>
  );
  const hasOrders = !!orders.length;

  const renderHistoricalOrder = useCallback<
    NonNullable<FlatListProps<IHistoricalOrder>['renderItem']>
  >(({ item }) => <Order order={item} />, []);

  // this is needed to immediately show the modal if we have unavailable items, the component
  // it's duplicated bc of the modal controller issue
  if (unavailableCartEntries.length > 0) {
    return (
      <RemoveUnavailableItemsModal
        onConfirmCb={handleUnavailableModalDismiss}
        unavailableCartEntries={unavailableCartEntries}
      />
    );
  }

  function getContent() {
    if (loading) {
      return <LoyaltyLoader />;
    }

    if (hasOrders) {
      return (
        <FlatList
          testID="inner-layout"
          ListHeaderComponent={
            <>
              <OfferDisclaimer>{formatMessage({ id: 'recentOrdersDisclaimer' })}</OfferDisclaimer>
              {refreshButton}
            </>
          }
          keyExtractor={historicalOrderKeyExtractor}
          data={orders}
          renderItem={renderHistoricalOrder}
        />
      );
    }

    return (
      <OrdersEmptyWrapper>
        <OrdersEmpty>{refreshButton}</OrdersEmpty>
      </OrdersEmptyWrapper>
    );
  }

  return (
    <DrawerPage headerText={formatMessage({ id: 'recentOrders' })}>
      {getContent()}

      {showOrderUnavailableModal ? (
        <RemoveUnavailableItemsModal
          onConfirmCb={handleUnavailableModalDismiss}
          unavailableCartEntries={unavailableCartEntries}
        />
      ) : null}
    </DrawerPage>
  );
};
